/**
 * Unit tables + token decoding.
 *
 * Three concept categories:
 *  - magnitude prefixes (scalar multipliers, glued to a number): k/m/b/t
 *  - data units (whole tokens): B (byte), bit, with SI prefixes K/M/G/T/P
 *  - time units (slotted): s min h d w mo y
 * Anything else is a custom unit (its own dimension, deduced on first use).
 *
 * See README.md for the full language spec.
 */

/** Magnitude letters, case-insensitive, glued to a number. Decimal SI powers.
 *  `g` = giga = billion (keeps `b`/`B` free to always mean byte). Counts top out
 *  at `g`; `T`/`P` are data-only (tera/peta-byte), handled by the data prefixes. */
export const MAGNITUDE: Record<string, number> = {
  k: 1e3,
  m: 1e6,
  g: 1e9,
};

/** Time units (always decimal/approx; mo and y are deliberate back-of-envelope rounds). */
export const TIME: Record<string, number> = {
  ms: 0.001,
  s: 1,
  min: 60,
  h: 3600,
  d: 86400,
  wk: 604800, // `w` is left free for a custom unit (writes); week is `wk`
  mo: 2592000, // 30 d
  y: 31536000, // 365 d
  // spelled-out aliases (plurals fold to these via resolveUnit): `5 years`, `2 months`
  second: 1,
  minute: 60,
  hour: 3600,
  day: 86400,
  week: 604800,
  month: 2592000,
  year: 31536000,
};

/** SI prefixes used inside data units (decimal, never 1024). */
const DATA_PREFIX: Record<string, number> = {
  "": 1,
  k: 1e3,
  m: 1e6,
  g: 1e9,
  t: 1e12,
  p: 1e15,
};

export type Family = "byte" | "bit" | "time" | "count" | "money";

export interface ResolvedUnit {
  dim: string; // physical dimension: "data" | "time" | custom-name
  factor: number; // multiply by this to reach canonical base (byte / second / 1)
  family: Family;
}

export interface GluedSuffix {
  mag: number; // scalar multiplier (1 when none)
  unit?: string; // trailing unit token, if any
}

/**
 * Match a data-unit token like B, KB, MB, bit, Mbit. Bit must be spelled `bit`;
 * `b`/`B` are always byte. Prefix is case-insensitive.
 * Returns null if not a data unit.
 */
export function matchData(tok: string): { factor: number; family: "byte" | "bit" } | null {
  const m = /^([kKmMgGtTpP]?)(bit|b|B)$/.exec(tok);
  if (!m) return null;
  const prefix = (m[1] ?? "").toLowerCase();
  const base = m[2] ?? "";
  const pf = DATA_PREFIX[prefix];
  if (pf === undefined) return null;
  if (base === "bit") return { factor: pf / 8, family: "bit" };
  return { factor: pf, family: "byte" };
}

/** Resolve a known unit ($/time/data) — null for anything custom. */
function resolveDirect(tok: string): ResolvedUnit | null {
  if (tok === "$") return { dim: "money", factor: 1, family: "money" };
  const t = TIME[tok];
  if (t !== undefined) return { dim: "time", factor: t, family: "time" };
  const d = matchData(tok);
  if (d) return { dim: "data", factor: d.factor, family: d.family };
  return null;
}

/**
 * Resolve a *unit* token (no magnitude — that only happens glued to a number).
 * Plural-tolerant: a token ≥3 chars ending in `s` is treated as its singular
 * (`reqs`→`req`, `users`→`user`, `bits`→`bit`→data), so dims always render singular.
 */
export function resolveUnit(tok: string): ResolvedUnit {
  const direct = resolveDirect(tok);
  if (direct) return direct;
  if (tok.length >= 3 && tok.endsWith("s")) {
    const singular = tok.slice(0, -1);
    const ds = resolveDirect(singular);
    if (ds) return ds;
    return { dim: singular, factor: 1, family: "count" };
  }
  return { dim: tok, factor: 1, family: "count" };
}

/**
 * Decode the glued suffix immediately following a number (no space).
 * Order: lone magnitude letter → whole time unit → data unit → magnitude+unit → custom.
 */
export function decodeGlued(s: string): GluedSuffix {
  // 1. lone magnitude letter (k/m/g), case-insensitive
  if (/^[kmgKMG]$/.test(s)) {
    return { mag: MAGNITUDE[s.toLowerCase()] ?? 1 };
  }
  // 2. whole time unit (min, mo, d, h, w, y, s)
  if (TIME[s] !== undefined) return { mag: 1, unit: s };
  // 3. data unit (KB, MB, bit, Mbit, ...)
  if (matchData(s)) return { mag: 1, unit: s };
  // 4. magnitude letter + trailing unit (e.g. 5kreq, 10MB already caught above)
  const m = /^([kmgKMG])(.+)$/.exec(s);
  if (m) {
    const mag = MAGNITUDE[(m[1] ?? "").toLowerCase()] ?? 1;
    return { mag, unit: m[2] };
  }
  // 5. bare custom unit glued (e.g. 10img)
  return { mag: 1, unit: s };
}

/** Physical base of a dim key (which may carry a #tag). */
export function baseOf(dimKey: string): "data" | "time" | "custom" | "money" {
  const name = dimKey.split("#")[0];
  if (name === "data") return "data";
  if (name === "time") return "time";
  if (name === "money") return "money";
  return "custom";
}

export function tagOf(dimKey: string): string | undefined {
  const i = dimKey.indexOf("#");
  return i === -1 ? undefined : dimKey.slice(i + 1);
}

export function nameOf(dimKey: string): string {
  return dimKey.split("#")[0] ?? dimKey;
}
